// Local scheduling (P4): schedules and holidays (revisioned records), the
// occurrence preview, run history, bundle sync status, and site location.
// Schedules execute on the appliance; the cloud only authors and previews
// them, with the same rule code (docs/modules/scheduling.md).
import type {
  HolidayData,
  ScheduleAction,
  ScheduleData,
  ScheduleRule,
} from './generated/protocol/schedule_bundle_payload.schema';
import { ApiError, call, callNoContent, type Site } from './fleetApi';

export type { HolidayData, ScheduleAction, ScheduleData, ScheduleRule };

/** A record as the API returns it: sync metadata plus content. */
export interface StoredRecord<T> {
  id: string;
  rev: number;
  base_rev: number;
  origin: 'cloud' | 'appliance';
  modified_by: string;
  modified_at: string;
  hlc: string;
  data: T;
  /** Schedules: the rule in words, e.g. "Weekly on Mon, Fri, at 18:00". */
  summary?: string;
}

export type StoredSchedule = StoredRecord<ScheduleData>;
export type StoredHoliday = StoredRecord<HolidayData>;

export interface Revision<T> {
  rev: number;
  base_rev: number;
  origin: 'cloud' | 'appliance';
  modified_by: string;
  modified_at: string;
  deleted?: boolean;
  data: T | null;
  overwritten_by?: string;
  overwritten_at?: string;
}

export type RunOutcome = 'success' | 'partial' | 'failed' | 'skipped_holiday' | 'missed';

export interface Occurrence {
  schedule_id: string;
  name: string;
  enabled: boolean;
  /** Local date (YYYY-MM-DD) in the site's time zone. */
  date: string;
  /** When it fires; null when the solar event doesn't happen that day. */
  at: string | null;
  holiday_skip: boolean;
  outcome: RunOutcome | null;
}

export interface RunAction {
  endpoint_id: string;
  action: string;
  status: 'ok' | 'error';
  code?: string;
  detail?: string;
  duration_ms?: number;
}

export interface ScheduleRun {
  run_id: string;
  schedule_id: string;
  schedule_rev: number;
  appliance_id: string;
  bundle_version: number | null;
  occurrence_date: string;
  scheduled_for: string;
  fired_at: string | null;
  outcome: RunOutcome;
  actions: RunAction[];
  received_at: string;
}

export interface ApplianceBundle {
  id: string;
  serial: string;
  name: string | null;
  online: boolean;
  version: number;
  applied_at: string | null;
  in_sync: boolean;
}

export interface BundleStatus {
  /** The server can sign bundles, so schedules can be changed. */
  enabled: boolean;
  latest: { version: number; generated_at: string } | null;
  appliances: ApplianceBundle[];
}

export type RecordKind = 'schedules' | 'holidays';

// ---------------------------------------------------------------- API

const base = (apiUrl: string, tenantId: string, siteId: string) =>
  `${apiUrl}/v1/tenants/${encodeURIComponent(tenantId)}/sites/${encodeURIComponent(siteId)}`;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const listOf =
  <K extends string>(key: K) =>
  (v: unknown): v is Record<K, unknown[]> =>
    isObject(v) && Array.isArray(v[key]);
const isRecord = (v: unknown): v is StoredRecord<unknown> =>
  isObject(v) && typeof v.id === 'string' && typeof v.rev === 'number';
const json = { 'Content-Type': 'application/json' };

export async function fetchRecords<T>(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  kind: RecordKind,
  signal?: AbortSignal,
): Promise<StoredRecord<T>[]> {
  const body = await call(
    `${base(apiUrl, tenantId, siteId)}/${kind}`,
    token,
    { signal },
    listOf(kind),
  );
  return body[kind] as StoredRecord<T>[];
}

export async function fetchRecord<T>(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  kind: RecordKind,
  id: string,
): Promise<StoredRecord<T>> {
  return (await call(
    `${base(apiUrl, tenantId, siteId)}/${kind}/${encodeURIComponent(id)}`,
    token,
    {},
    isRecord,
  )) as StoredRecord<T>;
}

/** Creates (no id) or replaces a record. baseRev is the revision edited. */
export async function saveRecord<T>(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  kind: RecordKind,
  data: T,
  existing?: { id: string; baseRev: number },
): Promise<StoredRecord<T>> {
  const url = existing
    ? `${base(apiUrl, tenantId, siteId)}/${kind}/${encodeURIComponent(existing.id)}`
    : `${base(apiUrl, tenantId, siteId)}/${kind}`;
  return (await call(
    url,
    token,
    {
      method: existing ? 'PUT' : 'POST',
      headers: json,
      body: JSON.stringify({ base_rev: existing?.baseRev ?? 0, data }),
    },
    isRecord,
  )) as StoredRecord<T>;
}

export async function deleteRecord(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  kind: RecordKind,
  id: string,
  baseRev: number,
): Promise<void> {
  await callNoContent(
    `${base(apiUrl, tenantId, siteId)}/${kind}/${encodeURIComponent(id)}?base_rev=${String(baseRev)}`,
    token,
    { method: 'DELETE' },
  );
}

export async function fetchRevisions<T>(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  kind: RecordKind,
  id: string,
): Promise<Revision<T>[]> {
  const body = await call(
    `${base(apiUrl, tenantId, siteId)}/${kind}/${encodeURIComponent(id)}/revisions`,
    token,
    {},
    listOf('revisions'),
  );
  return body.revisions as Revision<T>[];
}

export async function restoreRevision<T>(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  kind: RecordKind,
  id: string,
  rev: number,
  baseRev: number,
): Promise<StoredRecord<T>> {
  return (await call(
    `${base(apiUrl, tenantId, siteId)}/${kind}/${encodeURIComponent(id)}/restore`,
    token,
    { method: 'POST', headers: json, body: JSON.stringify({ rev, base_rev: baseRev }) },
    isRecord,
  )) as StoredRecord<T>;
}

export async function fetchOccurrences(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<Occurrence[]> {
  const q = new URLSearchParams({ from, to });
  const body = await call(
    `${base(apiUrl, tenantId, siteId)}/schedule-occurrences?${q.toString()}`,
    token,
    { signal },
    listOf('occurrences'),
  );
  return body.occurrences as Occurrence[];
}

export async function fetchRuns(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  opts: { limit?: number; scheduleId?: string } = {},
  signal?: AbortSignal,
): Promise<ScheduleRun[]> {
  const q = new URLSearchParams({ limit: String(opts.limit ?? 100) });
  if (opts.scheduleId) q.set('schedule_id', opts.scheduleId);
  const body = await call(
    `${base(apiUrl, tenantId, siteId)}/schedule-runs?${q.toString()}`,
    token,
    { signal },
    listOf('runs'),
  );
  return body.runs as ScheduleRun[];
}

export async function fetchBundleStatus(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  signal?: AbortSignal,
): Promise<BundleStatus> {
  return call(
    `${base(apiUrl, tenantId, siteId)}/schedule-status`,
    token,
    { signal },
    (v): v is BundleStatus => isObject(v) && Array.isArray(v.appliances),
  );
}

export async function updateSiteLocation(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  loc: { timezone: string; latitude: number | null; longitude: number | null },
): Promise<Site> {
  return call(
    base(apiUrl, tenantId, siteId),
    token,
    { method: 'PATCH', headers: json, body: JSON.stringify(loc) },
    (v): v is Site => isObject(v) && typeof v.id === 'string',
  );
}

/** A client-safe message for an API failure. */
export function scheduleErrorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
}

// ---------------------------------------------------------------- dates
// Date keys are 'YYYY-MM-DD' local dates in the site's time zone. Arithmetic
// is done in UTC so the browser's own DST never shifts them (from Rev 1).

const DAY = 86_400_000;

function keyToUtc(key: string): number {
  const [y = 0, m = 1, d = 1] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

export function addDays(key: string, days: number): string {
  return new Date(keyToUtc(key) + days * DAY).toISOString().slice(0, 10);
}

export function weekdayOf(key: string): number {
  return new Date(keyToUtc(key)).getUTCDay();
}

/** Today's date key in a time zone. */
export function todayIn(timezone: string, now: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** The 6-week grid (Sunday first) showing the month of key. */
export function monthGrid(key: string): string[] {
  const first = `${key.slice(0, 7)}-01`;
  const start = addDays(first, -weekdayOf(first));
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export function addMonths(key: string, n: number): string {
  const [y = 0, m = 1] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 10);
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function monthLabel(key: string): string {
  const [y = 0, m = 1] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString([], {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatDateKey(key: string): string {
  const [y = 0, m = 1, d = 1] = key.split('-').map(Number);
  return `${MONTHS[m - 1] ?? ''} ${String(d)}, ${String(y)}`;
}

/** Week-of-month label for nth-weekday rules ('1st'..'4th', or 'last'). */
export function nthWeekdayLabel(key: string): string {
  const nth = Math.floor((Number(key.slice(8, 10)) - 1) / 7) + 1;
  return nth >= 5 ? 'last' : (['1st', '2nd', '3rd', '4th'][nth - 1] ?? '');
}

export function formatTimeIn(iso: string, timezone: string): string {
  return new Date(iso).toLocaleTimeString([], {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateTimeIn(iso: string, timezone: string): string {
  return new Date(iso).toLocaleString([], {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

let zones: string[] | null = null;

/** IANA time zones the browser knows (UTC first). */
export function listTimezones(): string[] {
  if (!zones) {
    try {
      zones = Intl.supportedValuesOf('timeZone');
    } catch {
      zones = [];
    }
    if (!zones.includes('UTC')) zones = ['UTC', ...zones];
  }
  return zones;
}

// ---------------------------------------------------------------- labels
// Mirrors internal/schedule Rule.Describe, for the editor's live summary.

function plural(n: number, one: string, unit: string): string {
  return n === 1 ? one : `Every ${String(n)} ${unit}`;
}

export function describeRule(r: ScheduleRule): string {
  let when: string;
  if (r.trigger_type === 'standard') {
    when = `at ${r.local_time ?? ''}`;
  } else if (!r.astro_offset_minutes) {
    when = `at ${r.astro_event ?? ''}`;
  } else {
    const off = r.astro_offset_minutes;
    when = `${String(Math.abs(off))} min ${off < 0 ? 'before' : 'after'} ${r.astro_event ?? ''}`;
  }
  const n = Math.max(r.repeat_interval, 1);
  let rec: string;
  switch (r.repeat_pattern) {
    case 'none':
      return `Once on ${r.start_date}, ${when}`;
    case 'daily':
      rec = plural(n, 'Daily', 'days');
      break;
    case 'weekly': {
      const days = (r.repeat_weekdays?.length ? r.repeat_weekdays : [weekdayOf(r.start_date)])
        .slice()
        .sort((a, b) => a - b)
        .map((d) => WEEKDAYS[d] ?? '')
        .join(', ');
      rec = `${plural(n, 'Weekly', 'weeks')} on ${days}`;
      break;
    }
    case 'monthly':
      rec =
        r.monthly_mode === 'nth_weekday'
          ? `${plural(n, 'Monthly', 'months')} on the ${nthWeekdayLabel(r.start_date)} ${WEEKDAYS[weekdayOf(r.start_date)] ?? ''}`
          : `${plural(n, 'Monthly', 'months')} on day ${String(Number(r.start_date.slice(8, 10)))}`;
      break;
    case 'yearly': {
      const m = Number(r.start_date.slice(5, 7));
      rec = `${plural(n, 'Annually', 'years')} on ${MONTHS[m - 1] ?? ''} ${String(Number(r.start_date.slice(8, 10)))}`;
      break;
    }
  }
  if (r.end_date) rec += ` until ${r.end_date}`;
  return `${rec}, ${when}`;
}

/** A parameter value as text (IDs and numbers; anything else as JSON). */
export function paramText(v: unknown): string {
  if (v === undefined || v === null) return '';
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
    ? String(v)
    : JSON.stringify(v);
}

export const OUTCOME_LABELS: Record<RunOutcome, string> = {
  success: 'Ran',
  partial: 'Partly failed',
  failed: 'Failed',
  skipped_holiday: 'Skipped (holiday)',
  missed: 'Missed',
};

export const OUTCOME_COLORS: Record<RunOutcome, 'success' | 'warning' | 'error' | 'default'> = {
  success: 'success',
  partial: 'warning',
  failed: 'error',
  skipped_holiday: 'default',
  missed: 'warning',
};
